/**
 * Patient Search Modal Component
 * ================================
 * Modal for searching and selecting a patient to create a new chart.
 */

import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Search, User, Loader2 } from 'lucide-react';

import { Modal } from '@/components/ui/Modal';
import { Input } from '@/components/ui/Input';
import { listPatients } from '@/api/patients';
import { formatDate } from '@/lib/utils';
import type { PatientBrief } from '@/types';

// =============================================================================
// TYPES
// =============================================================================

export interface PatientSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectPatient: (patient: PatientBrief) => void;
  isLoading?: boolean;
}

// =============================================================================
// COMPONENT
// =============================================================================

export function PatientSearchModal({
  isOpen,
  onClose,
  onSelectPatient,
  isLoading = false,
}: PatientSearchModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');

  // Debounce search query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(searchQuery);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset search when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setDebouncedQuery('');
    }
  }, [isOpen]);

  const { data: patientsData, isLoading: isSearching } = useQuery({
    queryKey: ['patients', 'search', debouncedQuery],
    queryFn: () =>
      listPatients({
        query: debouncedQuery || undefined,
        is_active: true,
        page: 1,
        page_size: 10,
      }),
    enabled: isOpen && debouncedQuery.length >= 2,
  });

  const patients = patientsData?.items || [];
  const showResults = debouncedQuery.length >= 2;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Select Patient"
      description="Search for a patient by name, email, or phone number"
      size="lg"
      closeOnOverlayClick={!isLoading}
    >
      <div className="space-y-4">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            type="text"
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            autoFocus
            disabled={isLoading}
          />
        </div>

        {/* Search Results */}
        <div className="min-h-[300px] max-h-[400px] overflow-y-auto">
          {!showResults && (
            <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
              <Search className="w-12 h-12 mb-3 text-gray-300" />
              <p className="text-center">
                Start typing to search for patients
                <br />
                <span className="text-sm">(minimum 2 characters)</span>
              </p>
            </div>
          )}

          {showResults && isSearching && (
            <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
              <Loader2 className="w-8 h-8 mb-3 animate-spin text-primary-600" />
              <p>Searching patients...</p>
            </div>
          )}

          {showResults && !isSearching && patients.length === 0 && (
            <div className="flex flex-col items-center justify-center h-[300px] text-gray-500">
              <User className="w-12 h-12 mb-3 text-gray-300" />
              <p className="text-center">
                No patients found
                <br />
                <span className="text-sm">Try a different search term</span>
              </p>
            </div>
          )}

          {showResults && !isSearching && patients.length > 0 && (
            <div className="space-y-2">
              {patients.map((patient) => (
                <button
                  key={patient.id}
                  onClick={() => onSelectPatient(patient)}
                  disabled={isLoading}
                  className="w-full p-4 text-left border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Patient Name */}
                      <div className="flex items-center space-x-2 mb-2">
                        <User className="w-5 h-5 text-gray-400" />
                        <h3 className="font-semibold text-gray-900">
                          {patient.full_name}
                        </h3>
                      </div>

                      {/* Patient Details */}
                      <div className="space-y-1 text-sm text-gray-600">
                        <div className="flex items-center space-x-2">
                          <span className="text-gray-500">
                            {patient.first_name} {patient.last_name}
                          </span>
                        </div>
                      </div>

                      {/* Additional Info */}
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        {patient.date_of_birth && (
                          <span>DOB: {formatDate(patient.date_of_birth)}</span>
                        )}
                        {patient.chart_number && (
                          <span>Chart #: {patient.chart_number}</span>
                        )}
                      </div>
                    </div>

                    {/* Select Button Visual Indicator */}
                    <div className="ml-4">
                      <div className="px-3 py-1 text-sm text-primary-600 bg-primary-50 rounded-md">
                        Select
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
}
